import type { PositionRow } from '@/components/finest-custom-position-list/types'
import { cellNumber } from './creditPositions'
import { incomeGroupKeys } from './incomeSuggestions'
import type { AssetGroupKey, CreditPerson, IncomeGroupKey } from './types'

interface OverviewInput {
  people: CreditPerson[]
  incomeRows: Record<string, Record<IncomeGroupKey, PositionRow[]>>
  assetRows: Record<string, Record<AssetGroupKey, PositionRow[]>>
  expenses: Record<string, PositionRow[]>
  liabilities: Record<string, PositionRow[]>
  year: number
}

export interface HouseholdOverview {
  income: number
  expenses: number
  assets: number
  liabilities: number
  debtRatio: number | null
}

const assetValueKeys: Record<AssetGroupKey, string> = {
  investment: 'amount',
  real_estate: 'market_value',
}

function sumRows(rows: PositionRow[] | undefined, key: string): number {
  return (rows ?? []).reduce((sum, row) => sum + (cellNumber(row, key) ?? 0), 0)
}

function personIncome(rows: Record<IncomeGroupKey, PositionRow[]> | undefined, year: number): number {
  return incomeGroupKeys.reduce((total, group) => total + sumRows(rows?.[group], String(year)), 0)
}

function personAssets(rows: Record<AssetGroupKey, PositionRow[]> | undefined): number {
  return Object.entries(assetValueKeys).reduce(
    (total, [group, key]) => total + sumRows(rows?.[group as AssetGroupKey], key),
    0,
  )
}

export function householdOverview({
  people,
  incomeRows,
  assetRows,
  expenses,
  liabilities,
  year,
}: OverviewInput): HouseholdOverview {
  const total = (amount: (person: CreditPerson) => number): number =>
    people.reduce((sum, person) => sum + amount(person), 0)

  const assets = total((person) => personAssets(assetRows[person.id]))
  const debts = total((person) => sumRows(liabilities[person.id], 'amount'))

  return {
    income: total((person) => personIncome(incomeRows[person.id], year)),
    expenses: total((person) => sumRows(expenses[person.id], 'amount')),
    assets,
    liabilities: debts,
    debtRatio: assets > 0 ? debts / assets : null,
  }
}
